/**
 * <Input />
 *
 * Single-line text input with first-class accessibility:
 *   * Always renders an associated <label> (sr-only if the caller only wants
 *     a floating placeholder).
 *   * Supports `error` prop -- renders a red border AND aria-describedby
 *     wiring so screen readers announce the message.
 *   * Help text shows in neutral ink-muted below the input.
 *   * 48px+ height aligns with tap-target guidelines for senior users.
 */
import * as React from "react";
import { cn } from "../../lib/cn";

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** Visible label shown above the input. Use `hideLabel` to keep it for screen readers only. */
  label: string;
  hideLabel?: boolean;
  /** Optional helper text rendered below the input when no error is set. */
  hint?: string;
  /** If set, renders the input in an error state with the given message. */
  error?: string;
  /** Optional element rendered inside the input on the left (icon, prefix). */
  leadingAddon?: React.ReactNode;
  /** Optional element rendered inside the input on the right. */
  trailingAddon?: React.ReactNode;
  /** Visual size tier. Defaults to `md` (48px). */
  sizeVariant?: "md" | "lg";
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hideLabel,
      hint,
      error,
      leadingAddon,
      trailingAddon,
      id,
      className,
      sizeVariant = "md",
      ...rest
    },
    ref,
  ) => {
    const reactId = React.useId();
    const inputId = id ?? `input-${reactId}`;
    const describedById = hint || error ? `${inputId}-desc` : undefined;
    const hasError = Boolean(error);
    const heightClass = sizeVariant === "lg" ? "h-14 text-lg" : "h-12 text-base";

    return (
      <div className="w-full">
        <label
          htmlFor={inputId}
          className={cn(
            "mb-1.5 block font-semibold text-ink",
            hideLabel ? "sr-only" : "text-sm",
          )}
        >
          {label}
        </label>

        <div
          className={cn(
            "flex items-center w-full rounded-xl border-2 bg-white px-3 transition-colors",
            heightClass,
            hasError
              ? "border-brand focus-within:border-brand"
              : "border-surface-border focus-within:border-ink",
          )}
        >
          {leadingAddon ? (
            <span className="mr-2 text-ink-muted" aria-hidden="true">
              {leadingAddon}
            </span>
          ) : null}

          <input
            ref={ref}
            id={inputId}
            aria-invalid={hasError || undefined}
            aria-describedby={describedById}
            className={cn(
              "flex-1 min-w-0 border-0 bg-transparent outline-none",
              "placeholder:text-ink-subtle text-ink",
              "disabled:cursor-not-allowed disabled:opacity-50",
              className,
            )}
            {...rest}
          />

          {trailingAddon ? (
            <span className="ml-2 text-ink-muted" aria-hidden="true">
              {trailingAddon}
            </span>
          ) : null}
        </div>

        {(hint || error) && (
          <p
            id={describedById}
            className={cn(
              "mt-1.5 text-sm",
              hasError ? "text-brand font-medium" : "text-ink-muted",
            )}
          >
            {error ?? hint}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";
